const CHANNEL_NAME = 'auth-token-sync'
const LOCK_NAME = 'auth-token-refresh'

interface TokenMessage {
  type: 'TOKEN_REFRESHED' | 'TOKEN_CLEARED'
  authToken?: string
  refreshToken?: string
}

// In-memory state for same-tab coordination
let refreshPromise: Promise<{
  authToken: string
  refreshToken: string
}> | null = null
let refreshPromiseResolve:
  | ((tokens: { authToken: string; refreshToken: string }) => void)
  | null = null
let refreshPromiseReject: ((error: Error) => void) | null = null

// BroadcastChannel for cross-tab communication
const channel = new BroadcastChannel(CHANNEL_NAME)

// Broadcast new tokens to all tabs
export const broadcastTokenRefresh = (
  authToken: string,
  refreshToken: string
): void => {
  channel.postMessage({ type: 'TOKEN_REFRESHED', authToken, refreshToken })
}

// Broadcast logout to all tabs
export const broadcastLogout = (): void => {
  channel.postMessage({ type: 'TOKEN_CLEARED' })
}

// Wait for token refresh from another tab (with timeout)
export const waitForTokenRefresh = (
  timeoutMs = 10000
): Promise<{ authToken: string; refreshToken: string }> => {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      channel.removeEventListener('message', handler)
      reject(new Error('Token refresh timeout'))
    }, timeoutMs)

    const handler = (event: MessageEvent<TokenMessage>) => {
      if (event.data.type === 'TOKEN_REFRESHED') {
        clearTimeout(timeout)
        channel.removeEventListener('message', handler)
        resolve({
          authToken: event.data.authToken!,
          refreshToken: event.data.refreshToken!,
        })
      } else if (event.data.type === 'TOKEN_CLEARED') {
        clearTimeout(timeout)
        channel.removeEventListener('message', handler)
        reject(new Error('Session cleared'))
      }
    }

    channel.addEventListener('message', handler)
  })
}

// Check if refresh is in progress (same tab)
export const isRefreshInProgress = (): boolean => {
  return refreshPromise !== null
}

// Wait for ongoing refresh (same tab)
export const waitForRefresh = (): Promise<{
  authToken: string
  refreshToken: string
}> => {
  return refreshPromise!
}

// Execute token refresh with coordination (same tab + cross tab)
export const executeRefresh = (
  refreshFn: () => Promise<{ authToken: string; refreshToken: string }>
): Promise<{ authToken: string; refreshToken: string }> => {
  // If refresh is already in progress in this tab, wait for it
  if (refreshPromise) {
    return refreshPromise
  }

  // Create promise synchronously BEFORE any async operations
  refreshPromise = new Promise((resolve, reject) => {
    refreshPromiseResolve = resolve
    refreshPromiseReject = reject
  })

  // Start the actual refresh process
  ;(async () => {
    try {
      // Check if another tab is refreshing
      const lockState = await navigator.locks.query()
      const isLockedByOtherTab = lockState.held?.some(
        (lock) => lock.name === LOCK_NAME
      )

      if (isLockedByOtherTab) {
        // Wait for other tab and resolve with their tokens
        const tokens = await waitForTokenRefresh()
        refreshPromiseResolve?.(tokens)
        return
      }

      // Acquire lock and refresh
      await navigator.locks.request(LOCK_NAME, async () => {
        const result = await refreshFn()
        refreshPromiseResolve?.(result)
      })
    } catch (error) {
      refreshPromiseReject?.(error as Error)
    } finally {
      refreshPromise = null
      refreshPromiseResolve = null
      refreshPromiseReject = null
    }
  })()

  return refreshPromise
}

// Subscribe to token updates from other tabs
export const onTokenUpdate = (
  callback: (message: TokenMessage) => void
): (() => void) => {
  const handler = (event: MessageEvent<TokenMessage>) => callback(event.data)
  channel.addEventListener('message', handler)
  return () => channel.removeEventListener('message', handler)
}
